import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Chip,
  Divider,
  Slider,
  Stack,
  Typography,
} from '@mui/material';
import {
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';

// --------------------------------------------------------------------------
// Formatters (self-contained so the widget set has no cross-file coupling)
// --------------------------------------------------------------------------
export const usd0 = (v) =>
  v == null || Number.isNaN(Number(v)) ? '—' : `$${Math.round(Number(v)).toLocaleString()}`;
export const num = (v, d = 0) =>
  v == null || Number.isNaN(Number(v))
    ? '—'
    : Number(v).toLocaleString(undefined, { maximumFractionDigits: d });
export const pct = (v) =>
  v == null || Number.isNaN(Number(v)) ? '—' : `${(Number(v) * 100).toFixed(1)}%`;

const DIRECTION_TONE = {
  increase: 'success',
  pull_back: 'warning',
  saturated: 'error',
  hold: 'info',
};
const DIRECTION_LABEL = {
  increase: 'Scale up',
  pull_back: 'Pull back',
  saturated: 'At capacity',
  hold: 'On track',
};

// --------------------------------------------------------------------------
// RangeStat — the low / expected / high range pill (the core "honest range")
// --------------------------------------------------------------------------
export function RangeStat({ label, scenarios, metric, fmt = usd0, highlight = false }) {
  const expected = scenarios?.expected?.[metric];
  const low = scenarios?.low?.[metric];
  const high = scenarios?.high?.[metric];
  return (
    <Card variant="outlined" sx={{ flex: 1, minWidth: 150, borderRadius: 2 }}>
      <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
        <Typography variant="caption" color="text.secondary">
          {label}
        </Typography>
        <Typography variant={highlight ? 'h4' : 'h6'} sx={{ fontWeight: 700, lineHeight: 1.2 }}>
          {fmt(expected)}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {fmt(low)} – {fmt(high)}
        </Typography>
      </CardContent>
    </Card>
  );
}

// --------------------------------------------------------------------------
// ComGauge — cost of marketing vs goal, colored by efficiency band
// --------------------------------------------------------------------------
export function ComGauge({ com, goal }) {
  if (com == null) {
    return <Chip size="small" variant="outlined" label="CoM — n/a" />;
  }
  let color = 'default';
  if (goal) {
    const ratio = com / goal;
    color = ratio <= 0.85 ? 'success' : ratio <= 1.1 ? 'warning' : 'error';
  }
  return (
    <Chip
      size="small"
      color={color}
      label={`CoM ${pct(com)}${goal ? ` · goal ${pct(goal)}` : ''}`}
    />
  );
}

// --------------------------------------------------------------------------
// ForecastSummaryHeader — client-level range cards, net revenue leads
// --------------------------------------------------------------------------
export function ForecastSummaryHeader({ scenarios, goalCom }) {
  if (!scenarios) return null;
  return (
    <Stack spacing={1.5}>
      <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
        <RangeStat label="Net revenue (next month)" scenarios={scenarios} metric="net_revenue" highlight />
      </Stack>
      <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
        <RangeStat label="Leads" scenarios={scenarios} metric="leads" fmt={(v) => num(v, 0)} />
        <RangeStat label="Appointments" scenarios={scenarios} metric="appointments" fmt={(v) => num(v, 0)} />
        <RangeStat label="Booked" scenarios={scenarios} metric="booked" fmt={(v) => num(v, 0)} />
        <RangeStat label="Cost of marketing" scenarios={scenarios} metric="com" fmt={pct} />
        <RangeStat label="Spend" scenarios={scenarios} metric="spend" fmt={usd0} />
      </Stack>
      <Typography variant="caption" color="text.secondary">
        Estimates based on the client&apos;s own recent data — actual results vary.
        {goalCom != null ? ` Cost-of-marketing goal: ${pct(goalCom)}.` : ''}
      </Typography>
    </Stack>
  );
}

// --------------------------------------------------------------------------
// RecommendationCard — increase / pull-back / saturated / hold
// --------------------------------------------------------------------------
export function RecommendationCard({ rec }) {
  const tone = DIRECTION_TONE[rec.direction] || 'info';
  return (
    <Card
      variant="outlined"
      sx={{ borderLeft: 4, borderLeftColor: `${tone}.main`, borderRadius: 2 }}
    >
      <CardContent>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            {rec.headline}
          </Typography>
          <Chip size="small" color={tone} label={DIRECTION_LABEL[rec.direction] || rec.direction} />
        </Stack>
        <Typography variant="caption" color="text.secondary">
          {rec.scope_label}
        </Typography>

        {rec.direction !== 'hold' && (
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
            <Chip size="small" variant="outlined" label={`Now ${usd0(rec.current_budget)}/mo`} />
            <Typography color="text.secondary">→</Typography>
            <Chip size="small" color={tone} label={`Suggest ${usd0(rec.recommended_budget)}/mo`} />
          </Stack>
        )}

        <Box component="ul" sx={{ pl: 2, mt: 1, mb: 0 }}>
          {(rec.why || []).map((w, i) => (
            <Typography key={i} component="li" variant="body2" color="text.secondary">
              {w}
            </Typography>
          ))}
        </Box>
      </CardContent>
    </Card>
  );
}

// --------------------------------------------------------------------------
// DiminishingReturnsChart — projected leads across the spend range, with the
// current operating point marked. Concave when the elasticity fit found a real
// curve (k > 0); a straight line when CPL is flat (honest fallback).
// --------------------------------------------------------------------------
export function DiminishingReturnsChart({ segment, daysInMonth, appliedBudget, height = 140 }) {
  const curve = segment?.curve;
  if (!curve || !curve.cpl_at_base) return null;

  const floor = segment.spend_floor ?? segment.base_budget * 0.5;
  const ceiling = segment.spend_ceiling ?? segment.base_budget * 2;
  const span = Math.max(ceiling - floor, 1);
  const N = 24;
  const data = [];
  for (let i = 0; i <= N; i += 1) {
    const spend = floor + (span * i) / N;
    const daily = daysInMonth ? spend / daysInMonth : spend / 30;
    let cpl = curve.cpl_at_base;
    if (curve.k && curve.spend_base_daily && daily > 0) {
      cpl = curve.cpl_at_base * (1 + curve.k * Math.log(daily / curve.spend_base_daily));
    }
    const leads = cpl > 0 ? spend / cpl : 0;
    data.push({ spend: Math.round(spend), leads: Math.round(leads) });
  }

  return (
    <ResponsiveContainer width="100%" height={height}>
      <LineChart data={data} margin={{ top: 6, right: 8, left: 0, bottom: 0 }}>
        <XAxis dataKey="spend" tick={{ fontSize: 10 }} tickFormatter={(v) => `$${Math.round(v / 1000)}k`} />
        <YAxis tick={{ fontSize: 10 }} width={28} />
        <RechartsTooltip
          formatter={(value, name) => (name === 'leads' ? [num(value), 'Leads'] : [usd0(value), name])}
          labelFormatter={(v) => `Spend ${usd0(v)}`}
        />
        <Line type="monotone" dataKey="leads" stroke="#1976d2" strokeWidth={2} dot={false} isAnimationActive={false} />
        {appliedBudget != null && (
          <ReferenceLine x={Math.round(appliedBudget)} stroke="#9c27b0" strokeDasharray="4 3" />
        )}
      </LineChart>
    </ResponsiveContainer>
  );
}

// --------------------------------------------------------------------------
// SegmentRow — a budget slider + that segment's range + CoM gauge + curve
// --------------------------------------------------------------------------
export function SegmentRow({ segment, value, outputs, goalCom, daysInMonth, onChange }) {
  const floor = Math.max(0, Math.round(segment.spend_floor ?? segment.base_budget * 0.5));
  const ceiling = Math.round(segment.spend_ceiling ?? segment.base_budget * 2);
  const step = Math.max(50, Math.round((ceiling - floor) / 100));
  const scenarios = outputs || segment.outputs;

  return (
    <Card variant="outlined" sx={{ borderRadius: 2 }}>
      <CardContent>
        <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            {segment.scope_label || 'Segment'}
          </Typography>
          {segment.saturated && <Chip size="small" color="error" label="At capacity" />}
        </Stack>

        <Box sx={{ px: 1, mt: 1 }}>
          <Slider
            value={Math.min(Math.max(value, floor), ceiling)}
            min={floor}
            max={ceiling}
            step={step}
            onChange={(_e, v) => onChange(segment.budget_id, Array.isArray(v) ? v[0] : v)}
            valueLabelDisplay="auto"
            valueLabelFormat={usd0}
          />
          <Stack direction="row" justifyContent="space-between">
            <Typography variant="caption" color="text.secondary">{usd0(floor)}</Typography>
            <Typography variant="caption" sx={{ fontWeight: 700 }}>{usd0(value)}/mo</Typography>
            <Typography variant="caption" color="text.secondary">{usd0(ceiling)}</Typography>
          </Stack>
        </Box>

        <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
          <Box sx={{ flex: 1, minWidth: 160 }}>
            <Typography variant="caption" color="text.secondary">Net revenue</Typography>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              {usd0(scenarios?.expected?.net_revenue)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {usd0(scenarios?.low?.net_revenue)} – {usd0(scenarios?.high?.net_revenue)}
            </Typography>
          </Box>
          <ComGauge com={scenarios?.expected?.com} goal={goalCom} />
        </Stack>

        <DiminishingReturnsChart segment={segment} daysInMonth={daysInMonth} appliedBudget={value} />
      </CardContent>
    </Card>
  );
}

// --------------------------------------------------------------------------
// SegmentCard — one digestible unit per segment: the recommendation for THIS
// segment, its forecast performance, and its budget slider, all together
// (mirrors the Budget Center per-segment mental model).
// --------------------------------------------------------------------------
export function SegmentCard({ segment, rec, value, outputs, goalCom, daysInMonth, onChange }) {
  const direction = rec?.direction || 'hold';
  const tone = DIRECTION_TONE[direction] || 'info';
  const floor = Math.max(0, Math.round(segment.spend_floor ?? segment.base_budget * 0.5));
  const ceiling = Math.round(segment.spend_ceiling ?? segment.base_budget * 2);
  const step = Math.max(50, Math.round((ceiling - floor) / 100));
  const scenarios = outputs || segment.outputs;

  return (
    <Card variant="outlined" sx={{ borderRadius: 2, borderLeft: 5, borderLeftColor: `${tone}.main` }}>
      <CardContent>
        {/* Segment + its recommendation verdict */}
        <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              {segment.scope_label || 'Segment'}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'capitalize' }}>
              {segment.scope_mode}
            </Typography>
          </Box>
          <Chip size="small" color={tone} label={DIRECTION_LABEL[direction] || direction} />
        </Stack>

        {rec && (
          <Box sx={{ mt: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {rec.headline}
            </Typography>
            {direction !== 'hold' && (
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
                <Chip size="small" variant="outlined" label={`Now ${usd0(rec.current_budget)}/mo`} />
                <Typography color="text.secondary">→</Typography>
                <Chip size="small" color={tone} label={`Suggest ${usd0(rec.recommended_budget)}/mo`} />
              </Stack>
            )}
            <Box component="ul" sx={{ pl: 2, mt: 0.5, mb: 0 }}>
              {(rec.why || []).map((w, i) => (
                <Typography key={i} component="li" variant="caption" color="text.secondary">
                  {w}
                </Typography>
              ))}
            </Box>
          </Box>
        )}

        <Divider sx={{ my: 1.5 }} />

        {/* Forecast performance for this segment */}
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
          <Box sx={{ flex: 1, minWidth: 170 }}>
            <Typography variant="caption" color="text.secondary">Net revenue (next month)</Typography>
            <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
              {usd0(scenarios?.expected?.net_revenue)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {usd0(scenarios?.low?.net_revenue)} – {usd0(scenarios?.high?.net_revenue)}
            </Typography>
          </Box>
          <Box sx={{ minWidth: 90 }}>
            <Typography variant="caption" color="text.secondary">Leads</Typography>
            <Typography variant="body1" sx={{ fontWeight: 600 }}>
              {num(scenarios?.expected?.leads)}
            </Typography>
          </Box>
          <Box sx={{ minWidth: 110 }}>
            <Typography variant="caption" color="text.secondary">Appointments</Typography>
            <Typography variant="body1" sx={{ fontWeight: 600 }}>
              {num(scenarios?.expected?.appointments)}
            </Typography>
          </Box>
          <ComGauge com={scenarios?.expected?.com} goal={goalCom} />
        </Stack>

        {/* Budget slider for this segment */}
        <Box sx={{ px: 1, mt: 2 }}>
          <Typography variant="caption" color="text.secondary">
            Model a different budget for this segment
          </Typography>
          <Slider
            value={Math.min(Math.max(value, floor), ceiling)}
            min={floor}
            max={ceiling}
            step={step}
            onChange={(_e, v) => onChange(segment.budget_id, Array.isArray(v) ? v[0] : v)}
            valueLabelDisplay="auto"
            valueLabelFormat={usd0}
          />
          <Stack direction="row" justifyContent="space-between">
            <Typography variant="caption" color="text.secondary">{usd0(floor)}</Typography>
            <Typography variant="caption" sx={{ fontWeight: 700 }}>{usd0(value)}/mo</Typography>
            <Typography variant="caption" color="text.secondary">{usd0(ceiling)}</Typography>
          </Stack>
        </Box>

        <DiminishingReturnsChart segment={segment} daysInMonth={daysInMonth} appliedBudget={value} />
      </CardContent>
    </Card>
  );
}
